<template lang="pug">
content-page
	div(slot="nav")
		v-btn.hidden-md-and-up.mb-6(color="primary" outlined block nuxt exact :to="localePath({name:'gear'})") &laquo; terug naar materiaal
		gear-filter-list(:features="activeFeatures" :products="category.products" @filtered-products="setFilteredProducts")
	div.hidden-sm-and-down(slot="sidebar" style="max-width: 280px;")
		v-card.mb-6(outlined)
			.pa-3.accent--text
				strong(v-if="lastUpdateDate")
					v-icon.pr-2(small color="accent") info
					| {{$t('page.gear:category:infosheet_last_updated_{date}', {date: lastUpdateDate})}}
				p.caption {{$t('page.gear:category:infosheet_description')}}
				template(v-if="lastAddedProducts")
					strong {{$t('page.gear:category:infosheet_last_added')}}
					ul.list-unstyled
						li.d-flex(v-for="product in lastAddedProducts")
							ws-icon(color="accent" small) angle-right
							nuxt-link(:to="localePath({name: 'gear-category-product-product', params: { category: category.slug, product: product.slug }})"  :title="product.name")
								small {{product.name}}
	.flow-left
		h1 {{category.name}}
		ws-content(:value="category.content")
		.mb-6(v-if="category.clusters.length")
			h2 {{$t('page.gear:category:clusters_header')}}
			ul.list-unstyled
				li(v-for="cluster in category.clusters")
					nuxt-link(:to="localePath({name:'gear-category-cluster', params: {category: category.slug, cluster: cluster.slug}})")
						ws-icon(small) angle-right
						| {{cluster.name}}
	.flow-left(style="width:100%")
		v-divider.mb-6
		v-row
			v-col(v-if="filteredProducts.length === 0 && filtersLoaded")
				ws-icon.vertical-align-sub(small color="error") exclamation-circle
				span {{$t('page.gear.category:label.no.products')}}
			v-col(md="4" sm="6" v-for="product in filteredProducts" :key="product.id")
				nuxt-link.d-flex.flex-column.black--text(:to="localePath({name: 'gear-category-product-product', params: {product: product.slug}})")
					.d-flex.justify-space-between
						div
							h3 {{product.name}}
							h3.accent--text v.a. {{ $n(product.price, 'currency', $i18n.locale) }}
						v-sheet.ml-2.gear--product-rating.accent.white--text(tile v-if="category.id !== '32e32d17-179f-59a5-9b45-b8dea2a1b6ba' && product.rating") {{product.rating}}
					v-img(:src="`${product.mainImage}?width=640`" :aspect-ratio="16/9")
					.gear-line-fader.hidden-xs-only
						ws-content(:value="product.findings")
					ws-content.hidden-sm-and-up(:value="product.findings")
					hr.gear-product-divider.hidden-xs-only.mt-1.mb-3
					ul.list-unstyled.hidden-xs-only(v-if="product.aspects.length")
						li.d-flex(v-for="aspect in getPositiveAspects(product)" :key="aspect.id")
							ws-icon.gear-icon--align-start.mr-3(small color="success") plus
							.d-inline-block {{aspect.name}}
						li.d-flex(v-for="aspect in getNegativeAspects(product)" :key="aspect.id")
							ws-icon.gear-icon--align-start.mr-3(small color="error") minus
							.d-inline-block {{aspect.name}}
						li.d-flex(v-for="clusterProduct in bestInClusters(product)")
							ws-icon.gear-icon--align-start.mr-3.icon-trophy--gold(small color="gold") trophy
							.d-inline-block {{clusterProduct.cluster.name}}

</template>

<style lang="sass">
.gear--product-rating
	float: right
	text-align: center
	display: inline-block
	width: 36px
	height: 36px
	line-height: 36px
	font-size: 15px
	flex-shrink: 0

.gear-line-fader
	max-height: 6rem
	overflow: hidden
	position: relative
	&:after
		position: absolute
		content: ""
		width: 100%
		height: 2em
		z-index: 1
		top: 4em
		left: 0
		background: linear-gradient(0deg, #fff 0, hsla(0,0%,100%,0))

.gear-product-divider
	width: 50%
	margin-left: auto
	margin-right: auto
	border-top: none
	border-bottom: 1px dotted #ddd

.gear-icon--align-start
	align-self: start
	margin-top: 5px

.vertical-align-baseline
	vertical-align: baseline !important

.gear-infosheet
	border: 1px solid #ddd
</style>

<script>
import gql from 'graphql-tag';
import Content from '@dengl/common/components/content.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import GearFilterList from '@/components/gear/filter-list.vue';

const gearCategory = gql`
	query gearCategory($slug: String) {
		category: gearCategory(slug: $slug) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			features {
				isFilterActive
				sort
				feature {
					id
					name
					slug
					options {
						id
						slug
						name
					}
				}
			}
			clusters {
				id
				name
				slug
			}
			products {
				id
				slug
				name
				price
				rating
				mainImage
				updated_at
				created_at
				findings {
					...ContentDataFragment
				}
				featureValues {
					content {
						...ContentDataFragment
					}
					option {
						slug
						feature {
							slug
						}
					}
				}
				aspects {
					id
					name
					isPositive
				}
				clusters {
					rating
					sort
					product {
						id
					}
					cluster {
						id
						name
					}
				}
			}
			...MetaFragment
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/materiaal/:category',
			'nl-BE': '/materiaal/:category'
		}
	},
	components: {
		'gear-filter-list': GearFilterList
	},
	layout: 'default',
	asyncData({ params, app }) {
		return app.$graphClient
			.query({
				query: gearCategory,
				variables: {
					slug: params.category
				},
				errorPolicy: 'all',
				fetchPolicy: 'network-only'
			})
			.then(({ data, errors }) => {
				if (!data.category) {
					return app.httpError(404, null, 'gear-category-index-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.meta(data.category.meta);
				app.ads({});
				app.breadcrumbs({
					[app.i18n.t('page.gear:breadcrumb')]: {
						name: 'gear'
					},
					[data.category.name]: {
						name: 'gear-category'
					}
				});

				return data;
			})
			.catch(error => {
				return app.httpError(500, null, 'gear-category-index-02', error);
			});
	},
	data() {
		return {
			filteredProducts: [],
			filtersLoaded: false
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		lastUpdateDate() {
			const lastUpdate = Math.max.apply(
				null,
				this.category.products.map(product => {
					return new Date(product.updated_at);
				})
			);
			return this.$dayjs(lastUpdate).format('D MMM YYYY');
		},
		lastAddedProducts() {
			if (!this.category.products.length) return false;
			const sortedProducts = [...this.category.products].sort((a, b) => {
				return (
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
				);
			});
			return sortedProducts.slice(0, 2);
		},
		activeFeatures() {
			return this.category.features.filter(feature => feature.isFilterActive);
		}
	},
	methods: {
		getPositiveAspects(product) {
			return product.aspects.filter(a => a.isPositive);
		},
		getNegativeAspects(product) {
			return product.aspects.filter(a => !a.isPositive);
		},
		bestInClusters(product) {
			return product.clusters.filter(clusterProduct => {
				return (
					clusterProduct.sort === 0 && clusterProduct.product.id === product.id
				);
			});
		},
		setFilteredProducts(products) {
			if (!this.filtersLoaded) {
				this.filtersLoaded = true;
			}
			this.filteredProducts = products.sort((a, b) => {
				return b.rating - a.rating;
			});
		}
	}
};
</script>
